import { createContext, useContext, useEffect, useMemo, useRef, useState, ReactNode, PointerEvent as ReactPointerEvent } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useLocation, useNavigate } from "react-router-dom";
import { ChevronDown, ChevronUp, ChevronRight, ArrowLeft, MessagesSquare, Search, Briefcase, ExternalLink, Plus } from "lucide-react";
import { useChatList, useUnreadChatCount, usePartners, useProfile, useVacatures, useOrgContacts } from "../hooks/queries";
import { Chat, Partner, resolveChat } from "../api/api";
import ChatThread from "./ChatThread";
import { UserAvatar } from "./ui/user-avatar";
import { SectionLabel } from "./ui/section-label";
import { useAuth } from "../hooks/useAuth";
import { algemeenChatLabel } from "../lib/chat";
import { partnerIconUrl } from "../lib/partner";
import { cn } from "@/lib/utils";

interface VirtualAlgemeen {
  virtual: true;
  partnerId: number;
  partnerName: string;
}

type ChatRow = Chat | VirtualAlgemeen;

function isVirtual(row: ChatRow): row is VirtualAlgemeen {
  return "virtual" in row;
}

function isAlgemeen(c: Chat): boolean {
  return c.job_opening_id === null;
}

function rowIsAlgemeen(row: ChatRow): boolean {
  return isVirtual(row) || isAlgemeen(row);
}

function rowPartnerId(row: ChatRow): number {
  return isVirtual(row) ? row.partnerId : row.partner_id;
}

function rowPartnerName(row: ChatRow): string {
  return isVirtual(row) ? row.partnerName : row.partner_name;
}

function rowKey(row: ChatRow): string {
  return isVirtual(row) ? `virtual-${row.partnerId}` : `chat-${row.id}`;
}

function shortTime(iso: string): string {
  const d = new Date(iso);
  if (d.toDateString() === new Date().toDateString()) {
    return d.toLocaleTimeString("nl-NL", { hour: "2-digit", minute: "2-digit" });
  }
  return d.toLocaleDateString("nl-NL", { day: "numeric", month: "short" });
}

function UnreadBadge({ n, className }: { n: number; className?: string }) {
  if (n <= 0) return null;
  return (
    <span className={cn("inline-flex h-5 min-w-5 items-center justify-center rounded-pill bg-waves-koraal-hover px-1.5 text-xs font-bold text-white", className)}>
      {n > 9 ? "9+" : n}
    </span>
  );
}

interface ChatWidgetContextType {
  // Opens the chat widget on an Algemeen (general, not vacature-scoped)
  // chat. Algemeen chats are one-on-one between a single partner user and
  // Waves: partner_admin/hmp omit partnerId and land on their own chat
  // (see dto.ResolveChatRequest on the backend); admin/kam pass partnerId
  // to open the picker of that partner's users.
  openAlgemeenChat: (partnerId?: number) => void;
  // Opens the widget on an already-resolved chat, e.g. a vacature chat.
  openChat: (chat: Chat) => void;
}

const ChatWidgetContext = createContext<ChatWidgetContextType>({
  openAlgemeenChat: () => {},
  openChat: () => {},
});

export function useChatWidget() {
  return useContext(ChatWidgetContext);
}

// ── Horizontal docking ─────────────────────────────────────

const DOCK_EDGE = 24; // matches md:right-6
const DOCK_STORAGE_KEY = "chatWidget.right";
const DRAG_THRESHOLD = 4;
const OPEN_WIDTH = 760;
const COLLAPSED_WIDTH = 300;

function readStoredRight(): number {
  try {
    const v = Number(localStorage.getItem(DOCK_STORAGE_KEY));
    return Number.isFinite(v) && v > 0 ? v : DOCK_EDGE;
  } catch {
    return DOCK_EDGE;
  }
}

function clampRight(right: number, width: number): number {
  return Math.max(DOCK_EDGE, Math.min(right, window.innerWidth - width - DOCK_EDGE));
}

// Lets the widget be dragged left/right along the bottom of the screen on
// desktop (md+). The position is stored as a distance from the right edge
// and remembered per browser. On mobile the widget stays full width.
function useHorizontalDock() {
  const [right, setRight] = useState(readStoredRight);
  const [isDesktop, setIsDesktop] = useState(() => window.matchMedia("(min-width: 768px)").matches);
  const [, setViewportWidth] = useState(() => window.innerWidth);
  const draggedRef = useRef(false);

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 768px)");
    const onChange = () => setIsDesktop(mq.matches);
    const onResize = () => setViewportWidth(window.innerWidth);
    mq.addEventListener("change", onChange);
    window.addEventListener("resize", onResize);
    return () => {
      mq.removeEventListener("change", onChange);
      window.removeEventListener("resize", onResize);
    };
  }, []);

  function onPointerDown(e: ReactPointerEvent<HTMLElement>) {
    if (!isDesktop || e.button !== 0) return;
    // Don't hijack clicks on controls inside a drag handle.
    const interactive = (e.target as HTMLElement).closest("button, a, input, textarea, select");
    if (interactive && interactive !== e.currentTarget) return;
    const dockEl = e.currentTarget.closest<HTMLElement>("[data-chat-dock]");
    if (!dockEl) return;

    const handle = e.currentTarget;
    const startX = e.clientX;
    const width = dockEl.getBoundingClientRect().width;
    const startRight = clampRight(right, width);
    let current = startRight;
    draggedRef.current = false;
    handle.setPointerCapture(e.pointerId);

    const onMove = (ev: PointerEvent) => {
      const dx = ev.clientX - startX;
      if (!draggedRef.current && Math.abs(dx) < DRAG_THRESHOLD) return;
      draggedRef.current = true;
      current = clampRight(startRight - dx, width);
      setRight(current);
    };
    const onUp = () => {
      handle.removeEventListener("pointermove", onMove);
      handle.removeEventListener("pointerup", onUp);
      handle.removeEventListener("pointercancel", onUp);
      if (draggedRef.current) {
        try {
          localStorage.setItem(DOCK_STORAGE_KEY, String(current));
        } catch {
          // position just won't be remembered
        }
      }
    };
    handle.addEventListener("pointermove", onMove);
    handle.addEventListener("pointerup", onUp);
    handle.addEventListener("pointercancel", onUp);
  }

  // True (once) if the last pointer interaction was a drag, so the click
  // that follows it can be ignored.
  function consumeDrag(): boolean {
    const dragged = draggedRef.current;
    draggedRef.current = false;
    return dragged;
  }

  const style = (width: number) => (isDesktop ? { right: clampRight(right, width) } : undefined);
  const handleClass = isDesktop ? "cursor-grab select-none active:cursor-grabbing" : undefined;

  return { onPointerDown, consumeDrag, style, handleClass };
}

// "picker" is the partner users' vacature picker, "contacts" the Waves
// users' picker for starting an Algemeen chat with a partner user.
type PaneView = "thread" | "picker" | "contacts";

// Owns the chat widget's state and renders it. Wrap the authenticated part
// of the app in this once; use useChatWidget() anywhere inside to control
// it (e.g. open the Algemeen chat from a page's own "contact us" button).
//
// Algemeen chats are one-on-one between a single partner user and every
// Waves user (kam/admin) of that partner. Every role gets a list of
// conversations next to the open thread. Waves users see conversations
// grouped by partner — one Algemeen row per partner user who has one —
// and can start a new one via a partner → user picker. Partner users
// (hmp/partner_admin) see their own Algemeen chat and their partner's
// vacature chats, default to the Algemeen chat, and can start a vacature
// chat via a picker. For kam/partner_admin the backend only lists vacature
// chats they're involved in (sent a message, or creator/contactpersoon of
// the vacature); Algemeen chats are always listed.
export function ChatWidgetProvider({ children, enabled }: { children: ReactNode; enabled: boolean }) {
  const [open, setOpen] = useState(false);
  const [activeChat, setActiveChat] = useState<Chat | null>(null);
  const [resolvingKey, setResolvingKey] = useState<string | null>(null);
  const [paneView, setPaneView] = useState<PaneView>("thread");
  const [pickerQuery, setPickerQuery] = useState("");
  // Partner chosen in the contacts picker; null shows the partner list.
  const [contactsPartnerId, setContactsPartnerId] = useState<number | null>(null);
  const { user } = useAuth();
  const isPartnerUser = user?.role === "hmp" || user?.role === "partner_admin";
  const queryClient = useQueryClient();
  const location = useLocation();
  const navigate = useNavigate();
  const dock = useHorizontalDock();

  const { data: listData } = useChatList(enabled && open);
  const { data: unreadData } = useUnreadChatCount(enabled && !open);
  const { data: partnersData } = usePartners(enabled && open && !isPartnerUser && paneView === "contacts");
  const { data: partnerContacts } = useOrgContacts(
    enabled && open && !isPartnerUser && paneView === "contacts" && contactsPartnerId !== null,
    contactsPartnerId ?? undefined,
  );
  // Gated on `enabled` like every other query here: this provider also wraps
  // /login, and an unauthenticated /profile request 401s, which makes the API
  // client redirect to /login — an endless reload loop.
  const { data: profile } = useProfile(enabled);
  const { data: pickerVacatures } = useVacatures(
    { partner_id: user?.partner_id ?? undefined, limit: 500 },
    enabled && open && isPartnerUser && paneView === "picker",
  );

  // A partner user's own Algemeen chat is always listed; any other chat
  // only once it has a message, so a Waves user opening one from the
  // picker without writing doesn't leave an empty row for every colleague.
  const realChats: Chat[] = (listData?.chats ?? []).filter((c) => (isPartnerUser && isAlgemeen(c)) || c.last_message !== null);
  const partners: Partner[] = partnersData ?? [];
  const partnersWithAlgemeen = new Set(realChats.filter(isAlgemeen).map((c) => c.partner_id));
  // A partner user has exactly one Algemeen chat (their own), so they get a
  // virtual row until it exists. Waves users start new ones via the picker.
  const ownPartner = isPartnerUser && user?.partner_id != null ? [{ id: user.partner_id, name: "" }] : [];
  const virtualRows: VirtualAlgemeen[] = ownPartner
    .filter((p) => !partnersWithAlgemeen.has(p.id))
    .map((p) => ({ virtual: true, partnerId: p.id, partnerName: p.name }));

  // Grouped by partner (alphabetically), Algemeen first within each group,
  // then vacature chats by most recent activity.
  const groups = useMemo(() => {
    const rows: ChatRow[] = [...realChats, ...virtualRows];
    const byPartner = new Map<number, { name: string; rows: ChatRow[] }>();
    for (const row of rows) {
      const id = rowPartnerId(row);
      if (!byPartner.has(id)) byPartner.set(id, { name: rowPartnerName(row), rows: [] });
      byPartner.get(id)!.rows.push(row);
    }
    const lastActivity = (r: ChatRow) => (isVirtual(r) ? "" : r.last_message?.created_at ?? r.updated_at);
    return [...byPartner.values()]
      .sort((a, b) => a.name.localeCompare(b.name, "nl"))
      .map(g => ({
        ...g,
        rows: g.rows.sort((a, b) => {
          if (rowIsAlgemeen(a) !== rowIsAlgemeen(b)) return rowIsAlgemeen(a) ? -1 : 1;
          return lastActivity(b).localeCompare(lastActivity(a));
        }),
      }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listData, isPartnerUser, user?.partner_id]);

  const badgeCount = open ? realChats.reduce((sum, c) => sum + c.unread_count, 0) : unreadData?.count ?? 0;

  async function resolveAndActivate(params: { partner_id?: number; job_opening_id?: number; user_id?: number }, key: string) {
    if (resolvingKey !== null) return;
    setResolvingKey(key);
    try {
      const chat = await resolveChat(params);
      setActiveChat(chat);
      setPaneView("thread");
      queryClient.invalidateQueries({ queryKey: ["chats"] });
    } catch {
      // the global API client's error toast already surfaced this
    } finally {
      setResolvingKey(null);
    }
  }

  async function handleSelectRow(row: ChatRow) {
    if (!isVirtual(row)) {
      setActiveChat(row);
      setPaneView("thread");
      return;
    }
    // Only partner users get a virtual row (their own Algemeen chat), and
    // they must omit partner_id/user_id — the backend uses their session's.
    await resolveAndActivate({}, `partner-${row.partnerId}`);
  }

  function openContactsPicker(partnerId: number | null) {
    setPickerQuery("");
    setContactsPartnerId(partnerId);
    setPaneView("contacts");
  }

  function openAlgemeenChat(partnerId?: number) {
    setOpen(true);
    if (!isPartnerUser) {
      // Waves users have one Algemeen chat per partner user, so pick one.
      openContactsPicker(partnerId ?? null);
      return;
    }
    void resolveAndActivate({}, "partner-own");
  }

  function openChat(chat: Chat) {
    setActiveChat(chat);
    setPaneView("thread");
    setOpen(true);
  }

  function expand() {
    setOpen(true);
    // Partner users land on their Algemeen chat by default.
    if (isPartnerUser && !activeChat) openAlgemeenChat();
  }

  const contactName = profile?.waves_contact?.name ?? "Waves";
  const contactPictureUrl = profile?.waves_contact?.profile_picture_url ?? null;
  const hideOnRoute = location.pathname.startsWith("/swipe-review");

  // ── Shared pieces ──────────────────────────────────────────

  const chatRowButton = (row: ChatRow, showPartner: boolean) => {
    const virtual = isVirtual(row);
    const active = !virtual && activeChat?.id === row.id;
    const resolving = virtual && resolvingKey === `partner-${row.partnerId}`;
    const title = rowIsAlgemeen(row) ? (isPartnerUser ? `Algemeen · ${contactName}` : algemeenChatLabel(row as Chat)) : (row as Chat).vacature_title ?? "Vacature";
    const partnerIcon = virtual ? null : partnerIconUrl({ icon_url: row.partner_icon_url, logo_url: row.partner_logo_url });
    const preview = virtual ?(resolving ? "Openen…" : "Nog geen berichten") : row.last_message?.body ?? "Nog geen berichten";
    return (
      <button
        key={rowKey(row)}
        onClick={() => handleSelectRow(row)}
        disabled={resolving}
        className={cn(
          "flex w-full items-start gap-3 rounded-nav px-3 py-2.5 text-left transition-colors disabled:opacity-60",
          active ? "bg-waves-mist" : "hover:bg-waves-subtle",
        )}
      >
        {rowIsAlgemeen(row) && isPartnerUser ? (
          <UserAvatar name={contactName} pictureUrl={contactPictureUrl} size={32} className="mt-0.5" />
        ) : rowIsAlgemeen(row) && partnerIcon ? (
          <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-pill border border-waves-line bg-white p-1">
            <img src={partnerIcon} alt="" className="h-full w-full object-contain" />
          </span>
        ) : (
          <span className={cn("mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-pill", rowIsAlgemeen(row) ? "bg-waves-lucht" : "bg-status-neutral-bg")}>
            {rowIsAlgemeen(row) ? <MessagesSquare size={15} /> : <Briefcase size={15} />}
          </span>
        )}
        <span className="min-w-0 flex-1">
          <span className="flex items-baseline gap-2">
            <span className={cn("truncate text-sm", !virtual && row.unread_count > 0 ? "font-bold" : "font-semibold")}>{title}</span>
            {!virtual && <span className="ml-auto shrink-0 font-mono text-[11px] text-waves-muted">{shortTime(row.last_message?.created_at ?? row.updated_at)}</span>}
          </span>
          {showPartner && <span className="block truncate text-xs text-waves-muted">{rowPartnerName(row)}</span>}
          <span className="block truncate text-xs text-waves-muted">{preview}</span>
        </span>
        {!virtual && <UnreadBadge n={row.unread_count} className="mt-1 shrink-0" />}
      </button>
    );
  };

  const vacatureLink = activeChat && activeChat.job_opening_id !== null && (
    <button
      onClick={() => { navigate(`/vacature/${activeChat.job_opening_id}`); setOpen(false); }}
      className="flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-pill text-waves-muted hover:bg-waves-mist hover:text-waves-ink"
      aria-label="Bekijk vacature"
      title="Bekijk vacature"
    >
      <ExternalLink size={15} />
    </button>
  );

  const minimizeButton = (
    <button
      onClick={() => setOpen(false)}
      className="flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-pill text-waves-muted hover:bg-waves-mist hover:text-waves-ink"
      aria-label="Minimaliseren"
      title="Minimaliseren"
    >
      <ChevronDown size={18} />
    </button>
  );

  // ── Window ─────────────────────────────────────────────────
  //
  // Conversation list on the left, the open thread (or, for partner users,
  // the vacature picker) on the right. On mobile only one pane shows at a
  // time.

  const sidebarList = () => {
    if (!isPartnerUser) {
      return (
        <>
          {groups.length === 0 && <div className="py-8 text-center text-sm text-waves-muted">Geen gesprekken</div>}
          {groups.map(g => (
            <div key={g.name} className="flex flex-col gap-0.5">
              <SectionLabel className="px-3 pb-1.5 pt-1">{g.name}</SectionLabel>
              {g.rows.map(row => chatRowButton(row, false))}
            </div>
          ))}
          <button
            onClick={() => openContactsPicker(null)}
            className="flex shrink-0 items-center gap-2 rounded-nav border border-waves-zee bg-waves-mist px-3.5 py-2.5 text-left text-[13px] font-bold transition-colors hover:bg-waves-lucht"
          >
            <Plus size={15} /> Nieuw gesprek
          </button>
        </>
      );
    }
    const rows = groups.flatMap(g => g.rows);
    const algemeenRows = rows.filter(rowIsAlgemeen);
    const vacatureRows = rows.filter(r => !rowIsAlgemeen(r));
    return (
      <>
        <div className="flex flex-col gap-0.5">
          <SectionLabel className="px-3 pb-1.5 pt-1">Algemeen</SectionLabel>
          {algemeenRows.map(row => chatRowButton(row, false))}
        </div>
        <div className="flex flex-col gap-0.5">
          <SectionLabel className="px-3 pb-1.5 pt-1">Vacatures</SectionLabel>
          {vacatureRows.map(row => chatRowButton(row, false))}
          {vacatureRows.length === 0 && <div className="px-3 py-2 text-xs text-waves-muted">Nog geen vragen over vacatures</div>}
        </div>
        <button
          data-tour="chat-vac-btn"
          onClick={() => { setPickerQuery(""); setPaneView("picker"); }}
          className="flex shrink-0 items-center gap-2 rounded-nav border border-waves-zee bg-waves-mist px-3.5 py-2.5 text-left text-[13px] font-bold transition-colors hover:bg-waves-lucht"
        >
          <Briefcase size={15} /> Stel een vraag over een vacature
        </button>
      </>
    );
  };

  const vacaturePicker = () => {
    const vacatures = (pickerVacatures?.data ?? [])
      .filter(v => v.status !== "gesloten")
      .filter(v => !pickerQuery || v.functie.toLowerCase().includes(pickerQuery.toLowerCase()) || v.locatie?.toLowerCase().includes(pickerQuery.toLowerCase()));
    return (
      <div data-tour="chat-picker" className="flex h-full flex-col">
        <div className="shrink-0 px-4 pt-3">
          <div className="flex h-10 items-center gap-2 rounded-pill border border-waves-line px-4">
            <Search size={15} className="shrink-0 text-waves-muted" />
            <input
              autoFocus
              value={pickerQuery}
              onChange={e => setPickerQuery(e.target.value)}
              placeholder="Zoek vacature…"
              className="min-w-0 flex-1 bg-transparent text-sm outline-none placeholder:text-waves-muted"
            />
          </div>
        </div>
        <div className="flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto p-3">
          {!pickerVacatures && <div className="py-8 text-center text-sm text-waves-muted">Laden…</div>}
          {pickerVacatures && vacatures.length === 0 && <div className="py-8 text-center text-sm text-waves-muted">Geen vacatures gevonden</div>}
          {vacatures.map(v => (
            <button
              key={v.id}
              disabled={resolvingKey !== null}
              onClick={() => void resolveAndActivate({ partner_id: v.partner_id ?? undefined, job_opening_id: v.id }, `vacature-${v.id}`)}
              className="flex items-center gap-3 rounded-nav px-3 py-2.5 text-left transition-colors hover:bg-waves-subtle disabled:opacity-60"
            >
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-bold">{v.functie}</span>
                <span className="block truncate font-mono text-xs text-waves-muted">#{v.id}{v.locatie ? ` · ${v.locatie}` : ""}</span>
              </span>
              {resolvingKey === `vacature-${v.id}` && <span className="text-xs text-waves-muted">Openen…</span>}
            </button>
          ))}
        </div>
      </div>
    );
  };

  // Waves users' two-step picker for an Algemeen chat: partner, then one of
  // its partner users (hmp/partner_admin). Resolving reuses the existing
  // chat with that user if there is one.
  const contactsPicker = () => {
    const q = pickerQuery.toLowerCase();
    const choosingPartner = contactsPartnerId === null;
    const filteredPartners = partners.filter(p => !q || p.name.toLowerCase().includes(q));
    const contacts = (partnerContacts ?? []).filter(u => !q || u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q));
    const loading = choosingPartner ? !partnersData : !partnerContacts;
    const rowClass = "flex items-center gap-3 rounded-nav px-3 py-2.5 text-left transition-colors hover:bg-waves-subtle disabled:opacity-60";
    return (
      <div className="flex h-full flex-col">
        <div className="shrink-0 px-4 pt-3">
          <div className="flex h-10 items-center gap-2 rounded-pill border border-waves-line px-4">
            <Search size={15} className="shrink-0 text-waves-muted" />
            <input
              key={contactsPartnerId ?? "partners"}
              autoFocus
              value={pickerQuery}
              onChange={e => setPickerQuery(e.target.value)}
              placeholder={choosingPartner ? "Zoek partner…" : "Zoek gebruiker…"}
              className="min-w-0 flex-1 bg-transparent text-sm outline-none placeholder:text-waves-muted"
            />
          </div>
        </div>
        <div className="flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto p-3">
          {loading && <div className="py-8 text-center text-sm text-waves-muted">Laden…</div>}
          {choosingPartner ? (
            <>
              {!loading && filteredPartners.length === 0 && <div className="py-8 text-center text-sm text-waves-muted">Geen partners gevonden</div>}
              {filteredPartners.map(p => (
                <button key={p.id} onClick={() => openContactsPicker(p.id)} className={rowClass}>
                  <span className="min-w-0 flex-1 truncate text-sm font-bold">{p.name}</span>
                  <ChevronRight size={15} className="shrink-0 text-waves-muted" />
                </button>
              ))}
            </>
          ) : (
            <>
              {!loading && contacts.length === 0 && <div className="py-8 text-center text-sm text-waves-muted">Geen gebruikers gevonden</div>}
              {contacts.map(u => (
                <button
                  key={u.id}
                  disabled={resolvingKey !== null}
                  onClick={() => void resolveAndActivate({ partner_id: contactsPartnerId ?? undefined, user_id: u.id }, `user-${u.id}`)}
                  className={rowClass}
                >
                  <UserAvatar name={u.name} size={32} />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-bold">{u.name}</span>
                    <span className="block truncate text-xs text-waves-muted">{u.email}</span>
                  </span>
                  {resolvingKey === `user-${u.id}` && <span className="text-xs text-waves-muted">Openen…</span>}
                </button>
              ))}
            </>
          )}
        </div>
      </div>
    );
  };

  const chatWindow = () => {
    const pickingVacature = isPartnerUser && paneView === "picker";
    const pickingContact = !isPartnerUser && paneView === "contacts";
    const picking = pickingVacature || pickingContact;
    const showPane = activeChat !== null || picking;
    const partnerAlgemeen = isPartnerUser && !picking && activeChat !== null && isAlgemeen(activeChat);

    let title = "Chat";
    let subtitle = "Selecteer een gesprek";
    if (pickingVacature) {
      title = "Vraag over een vacature";
      subtitle = "Kies de vacature waar je vraag over gaat";
    } else if (pickingContact) {
      title = "Nieuw gesprek";
      subtitle = contactsPartnerId === null
        ? "Kies een partner"
        : `${partners.find(p => p.id === contactsPartnerId)?.name ?? "Partner"} · kies een gebruiker`;
    } else if (activeChat && isAlgemeen(activeChat)) {
      title = isPartnerUser ? contactName : algemeenChatLabel(activeChat);
      subtitle = isPartnerUser ? "Je contactpersoon bij Waves" : `Algemeen · ${activeChat.partner_name}`;
    } else if (activeChat) {
      title = activeChat.vacature_title ?? "Vacature";
      subtitle = [isPartnerUser ? "Vacature" : activeChat.partner_name, activeChat.vacature_location].filter(Boolean).join(" · ");
    }

    // In the contacts picker, back goes from a partner's users to the
    // partner list first.
    const pickerBack = () => {
      if (pickingContact && contactsPartnerId !== null) openContactsPicker(null);
      else setPaneView("thread");
    };

    return (
      <div className="flex min-h-0 flex-1">
        <div className={cn("flex w-full shrink-0 flex-col border-r border-waves-line md:w-[260px]", showPane && "hidden md:flex")}>
          <div className={cn("flex shrink-0 items-center gap-2 border-b border-waves-line px-5 py-3.5", dock.handleClass)} onPointerDown={dock.onPointerDown}>
            <div className="flex-1 text-[15px] font-bold">Gesprekken</div>
            <span className="md:hidden">{minimizeButton}</span>
          </div>
          <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto p-3">
            {sidebarList()}
          </div>
        </div>

        <div className={cn("min-w-0 flex-1 flex-col", showPane ? "flex" : "hidden md:flex")}>
          <div className={cn("flex shrink-0 items-center gap-3 border-b border-waves-line px-5 py-3.5", dock.handleClass)} onPointerDown={dock.onPointerDown}>
            {picking ? (
              <button onClick={pickerBack} className="flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-pill hover:bg-waves-mist" aria-label="Terug">
                <ArrowLeft size={17} />
              </button>
            ) : activeChat && (
              <button onClick={() => setActiveChat(null)} className="flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-pill hover:bg-waves-mist md:hidden" aria-label="Terug">
                <ArrowLeft size={17} />
              </button>
            )}
            {partnerAlgemeen && (
              <div className="relative shrink-0">
                <UserAvatar name={contactName} pictureUrl={contactPictureUrl} size={40} />
                <span className="absolute bottom-0 right-0 h-[11px] w-[11px] rounded-full border-2 border-white bg-status-ok-fg" />
              </div>
            )}
            <div className="flex min-w-0 flex-1 flex-col gap-px">
              <div className="truncate text-[15px] font-bold">{title}</div>
              <div className="truncate text-xs text-waves-muted">{subtitle}</div>
            </div>
            {!picking && vacatureLink}
            <span className={cn(!showPane && "hidden md:inline-flex")}>{minimizeButton}</span>
          </div>
          <div className="min-h-0 flex-1">
            {picking ? (
              pickingVacature ? vacaturePicker() : contactsPicker()
            ) : activeChat ? (
              <ChatThread chat={activeChat} />
            ) : (
              <div className="flex h-full items-center justify-center text-sm text-waves-muted">
                {resolvingKey !== null ? "Chat openen…" : "Selecteer een gesprek"}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <ChatWidgetContext.Provider value={{ openAlgemeenChat, openChat }}>
      {children}
      {enabled && open && (
        <div
          role="dialog"
          aria-label="Berichten"
          data-chat-dock
          data-tour="chat-window"
          className={cn(
            "fixed bottom-0 right-4 z-[45] flex max-h-[calc(100dvh-40px)] w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-t-[16px] border border-b-0 border-waves-line bg-white text-waves-ink md:right-6",
            "h-[600px] md:w-[760px]",
          )}
          style={{ boxShadow: "0 -12px 48px rgba(16,32,47,0.18)", ...dock.style(OPEN_WIDTH) }}
        >
          {chatWindow()}
        </div>
      )}
      {enabled && !open && !hideOnRoute && (
        <button
          onClick={() => { if (!dock.consumeDrag()) expand(); }}
          onPointerDown={dock.onPointerDown}
          data-chat-dock
          data-tour="chatbar"
          className={cn("fixed bottom-0 right-4 z-[45] flex w-[260px] items-center gap-3 rounded-t-[14px] border border-b-0 border-waves-line bg-white px-4 py-2.5 text-left transition-colors hover:bg-waves-subtle md:right-6 md:w-[300px]", dock.handleClass)}
          style={{ boxShadow: "0 -6px 24px rgba(16,32,47,0.10)", ...dock.style(COLLAPSED_WIDTH) }}
          aria-label="Berichten openen"
        >
          {isPartnerUser ? (
            <span className="relative shrink-0">
              <UserAvatar name={contactName} pictureUrl={contactPictureUrl} size={36} />
              <span className="absolute bottom-0 right-0 h-2.5 w-2.5 rounded-full border-2 border-white bg-status-ok-fg" />
            </span>
          ) : (
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-pill bg-waves-lucht"><MessagesSquare size={17} /></span>
          )}
          <span className="text-[15px] font-bold">Berichten</span>
          <UnreadBadge n={badgeCount} />
          <ChevronUp size={16} className="ml-auto text-waves-muted" />
        </button>
      )}
    </ChatWidgetContext.Provider>
  );
}
